import React from 'react';
import Parcel from 'single-spa-react/parcel';
import { mountRootParcel } from 'single-spa';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { StateParams } from '../../store/reducers';
import { LanguageCodes } from '../../models';
import { setLanguagePreference } from '../../store/actions/account.action';

// System is provided globally by the SystemJS runtime loaded in index.html.
// Accessed via window to avoid CRA's no-restricted-properties lint rule on System.import.
const systemImport = (name: string): Promise<any> =>
    (window as any).System.import(name);

const CHECK_IN_LOGIN_SOURCE = 'self-check-in-link';

const normalizeLanguageCode = (language?: string): LanguageCodes | undefined => {
    if (!language) return undefined;

    const normalizedLanguage = language.trim().toLowerCase().replace('-', '_');
    const languageMap: Record<string, LanguageCodes> = {
        en_us: LanguageCodes.EN_US,
        es: LanguageCodes.ES,
        ru: LanguageCodes.RU,
        pl: LanguageCodes.PL,
        hi: LanguageCodes.HI,
        gu: LanguageCodes.GU,
    };

    return languageMap[normalizedLanguage];
};

const getPreferredLanguageFromSession = (): LanguageCodes | undefined => {
    const personalInformation = sessionStorage.getItem('personal_information');
    if (!personalInformation) return undefined;

    try {
        const parsedPersonalInformation = JSON.parse(personalInformation);
        return normalizeLanguageCode(parsedPersonalInformation?.preferred_language);
    } catch (error) {
        console.error('PatientFormsParcel: failed to parse personal_information from session storage', error);
        return undefined;
    }
};

const PatientFormsParcel: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { patientId } = useParams<{ patientId: string }>();
    const returnTo = (location.state as { returnTo?: string } | null)?.returnTo ?? '/screenerQuestion';

    const practiceId    = useSelector((state: StateParams) => state.account.practiceId);
    const loginSource   = useSelector((state: StateParams) => state.account.loginSource);
    const treatmentId   = useSelector((state: StateParams) => state.signup.treatmentId);
    const appointmentId = useSelector((state: StateParams) => state.signup.appointmentId);
    const checkInId     = useSelector((state: StateParams) => state.signup.checkInId);
    const checkInVerificationResponse = useSelector((state: StateParams) => state.signup.checkInVerificationResponse);

    const resolvedPracticeId = practiceId ?? checkInVerificationResponse?.patientCheckInRecord?.practiceId;
    const resolvedLoginSource = loginSource ?? CHECK_IN_LOGIN_SOURCE
    const resolvedAppointmentId = appointmentId ?? checkInVerificationResponse?.appointment?.id;
    const resolvedCheckInId = checkInId ?? checkInVerificationResponse?.patientCheckInRecord?.id;

    if (!patientId || !resolvedPracticeId || !resolvedLoginSource) {
        console.error('PatientFormsParcel: missing required context (patientId, practiceId, loginSource)');
        return null;
    }

    return (
        <Parcel
            config={() => systemImport('@Smalljoys/PatientForms')}
            mountParcel={mountRootParcel}
            patientId={patientId}
            practiceId={resolvedPracticeId}
            loginSource={resolvedLoginSource}
            {...(treatmentId   && { treatmentId })}
            {...(resolvedAppointmentId && { appointmentId: resolvedAppointmentId })}
            {...(resolvedCheckInId     && { checkInId: resolvedCheckInId })}
            onComplete={() => {
                console.log('PatientFormsParcel: onComplete');
                const preferredLanguage = getPreferredLanguageFromSession();
                if (preferredLanguage) {
                    dispatch(setLanguagePreference({ languagePreference: preferredLanguage }));
                }
                localStorage.removeItem("sj:patient-session-id");
                Object.keys(sessionStorage).filter(k => k.startsWith('pf_')).forEach(k => sessionStorage.removeItem(k));
                navigate(returnTo, { replace: true });
            }}
        />
    );
};

export default PatientFormsParcel;
